import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ColumnSettingsContext } from '../../../services/columnSettings/columnSettings';
import PlaylistColumns, {
  ColumnScope,
  columnScreenSize,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { ScreenDetectionContext } from '../../../services/screenDetection/screenDetection';
import useShortcut from '../../../services/shortcuts/useShortcut';
import ViewOptionsDialog, { ViewOptionColumn } from './viewOptionsDialog';

interface Actions {
  hideColumn(column: TrackProperty): void;
  unhideColumn(column: TrackProperty): void;
  resetColumnWidths(): void;
}

/**
 * The View Options dialog for one list: its state, its shortcut (ctrl+J,
 * iTunes' ⌘J) and the element to render. The four list kinds - playlists,
 * track lists, cloud folders, search results - each call this with the
 * hide/unhide/reset they already get from useDeductedContentWidth, and put
 * "View options…" in their header menu.
 */
export default function useViewOptions(scope: ColumnScope, actions: Actions) {
  const [open, setOpen] = useState(false);
  const colCtx = useContext(ColumnSettingsContext);
  const screenSize = columnScreenSize(useContext(ScreenDetectionContext));

  const settings = colCtx.columnSettingsStore[scope][screenSize];

  const columns = useMemo<ViewOptionColumn[]>(() => {
    if (!settings) {
      return [];
    }

    return (Object.keys(settings) as TrackProperty[])
      .filter((key) => PlaylistColumns[key])
      .sort((a, b) => settings[a]!.sortOrder - settings[b]!.sortOrder)
      .map((key) => ({
        key,
        name: PlaylistColumns[key].name,
        visible: settings[key]!.width > 0,
      }));
  }, [settings]);

  useShortcut({
    id: 'view-options',
    keys: { key: 'j', mod: true },
    group: 'Library',
    description: 'View options: choose the columns',
    whileTyping: true,
    run: () => {
      setOpen((x) => !x);
    },
  });

  const openViewOptions = useCallback(() => setOpen(true), []);

  const viewOptionsDialog = open ? (
    <ViewOptionsDialog
      columns={columns}
      onToggle={(key, visible) => (visible ? actions.unhideColumn(key) : actions.hideColumn(key))}
      onReorder={(order) => colCtx.setColumnOrder(scope, order)}
      onReset={actions.resetColumnWidths}
      onClose={() => setOpen(false)}
    />
  ) : null;

  return { openViewOptions, viewOptionsDialog };
}
